// 9fbef606107a605d69c0edbcd8029e5d
import React from 'react';
import PropTypes from 'prop-types';
import isEmpty from 'lodash/isEmpty';
import Anchor from '@tcp/core/src/components/common/atoms/Anchor';
import BodyCopy from '@tcp/core/src/components/common/atoms/BodyCopy';
import { getViewportInfo } from '@tcp/core/src/utils';
import { callNavigateAPIForExternalLinks } from '@tcp/core/src/utils/utils';
import Row from '@tcp/core/src/components/common/atoms/Row';
import withStyles from '@tcp/core/src/components/common/hoc/withStyles';
import { getLabelFromCopyOrFromTranslation } from '@tcp/core/src/utils/labels/labelUtilty';
import pushEventToAdobeAnalytics from '@tcp/core/src/adobeAnalytics/adobe.utils';
import {
  GlobalAccessibilityLabels,
  GLOBAL_ACCESSIBILITY_LABELS,
  previousButton,
} from '@tcp/core/src/utils/labels/globalAccessibility.labels';
import style from './L3Panel.style';
import Drawer from '../Drawer';
import PromoBadge from '../PromoBadge';
import { keyboard } from '../../../../../../constants/constants';
import L3PanelConstants from './L3Panel.constants';

/* Method to close L3Drawer on keydown(ENTER and SPACE) */
const keydownHideL3Drawer = (e, hideL3Drawer) => {
  const { KEY_ENTER, KEY_SPACE } = keyboard;
  if ((e.which === KEY_ENTER || e.which === KEY_SPACE) && typeof hideL3Drawer === 'function') {
    hideL3Drawer();
  }
};

const fetchPromoBadge = (mainCategory) => {
  const { isDesktop, isTablet, isMobile } = getViewportInfo() || {};
  const promoBadgeValue = mainCategory?.promoBadge || '';
  const promoBadgeMobileValue = mainCategory?.promoBadgeMobile || '';

  if (isMobile && !isEmpty(promoBadgeMobileValue)) return promoBadgeMobileValue;
  if (isDesktop || isTablet) return promoBadgeValue;
  return '';
};

const renderPromoBadge = (
  promoBadge,
  currentIndex,
  brandThemeColor,
  shouldRenderMobileNavRedesignedView
) => {
  return (
    promoBadge && (
      <span className="nav-bar-item-content" data-locator={`promo_badge_${currentIndex}`}>
        <PromoBadge
          data={promoBadge}
          brandThemeColor={brandThemeColor}
          shouldRenderMobileNavRedesignedView={shouldRenderMobileNavRedesignedView}
        />
      </span>
    )
  );
};

const fireAnalytics = (name, l3Name, analyticsData) => {
  const AdobeTrackingData = `${analyticsData}-${name?.toLowerCase()}-${l3Name?.toLowerCase()}`;
  pushEventToAdobeAnalytics('header_click', AdobeTrackingData);
};

const L3Panel = (props) => {
  const {
    id,
    hideL3Drawer,
    hideL2Drawer,
    closeNav,
    name,
    shopalllink,
    shopallaspath,
    className,
    links = [],
    open,
    close,
    isShowNavAnimation,
    setSelectedL1Index,
    setHideDrawerInfoSection,
    shouldRenderMobileNavRedesignedView,
    analyticsData,
    brandThemeColor,
  } = props;
  const previousButtonLabel = getLabelFromCopyOrFromTranslation(
    `${GLOBAL_ACCESSIBILITY_LABELS}.${previousButton}`,
    GlobalAccessibilityLabels
  );
  const drawerWidthMobileAndTablet = shouldRenderMobileNavRedesignedView ? '343px' : '314px';

  return (
    <Drawer
      className={className}
      id={id}
      small
      medium
      large
      open={open}
      close={close}
      width={{
        small: drawerWidthMobileAndTablet,
        medium: drawerWidthMobileAndTablet,
        large: '100%',
      }}
      position={{
        top: 0,
        left: 0,
      }}
      height="100%"
      navigationLevel="l3"
      isShowNavAnimation={isShowNavAnimation}
      isMobileNavigationRedesignEnabled={shouldRenderMobileNavRedesignedView}
    >
      <div
        data-locator="overrlay_img"
        className={`${className}${
          shouldRenderMobileNavRedesignedView ? ' nav-bar-l3-panel-new-design' : ''
        } nav-bar-l3-panel`}
      >
        <div className="l3-panel-header">
          <span
            role="button"
            aria-label={previousButtonLabel}
            tabIndex={0}
            className="icon-back"
            onClick={(e) =>
              setTimeout(() => {
                hideL3Drawer(e);
              }, 0)
            }
            onKeyDown={(e) => keydownHideL3Drawer(e, hideL3Drawer)}
          />
          <span className="l1-label l3-label">{name}</span>
        </div>
        <Row className="nav-bar-l3-details" tabIndex={0}>
          <ul>
            <li>
              <Anchor
                to={`${shopalllink}&shopall=shop all`}
                data-locator="l3_link_shop_all"
                asPath={shopallaspath}
                handleLinkClick={() => {
                  closeNav();
                  fireAnalytics(name, L3PanelConstants.shopAllLabel, analyticsData);
                }}
                useRouterNavigation
              >
                <BodyCopy
                  className="l2-nav-link"
                  fontFamily="secondary"
                  fontSize={['fs13', 'fs13', 'fs14']}
                  lineHeight="lh107"
                  color="text.primary"
                >
                  <span
                    className={`${
                      shouldRenderMobileNavRedesignedView
                        ? 'nav-bar-item-label-mobile-redesign'
                        : 'nav-bar-item-label'
                    } full-width`}
                  >
                    {L3PanelConstants.shopAllLabel}
                  </span>
                </BodyCopy>
              </Anchor>
            </li>
            {links.map((l3Links, index) => {
              const {
                categoryContent: { displayToCustomer, name: l3Name, mainCategory } = {},
                url,
                asPath,
              } = l3Links || {};
              if (!displayToCustomer) return null;
              const promoBadge = fetchPromoBadge(mainCategory);
              return (
                <li key={`l3_link_${index.toString()}`}>
                  <Anchor
                    to={url}
                    asPath={asPath?.replace('#redirect', '')}
                    useRouterNavigation={true}
                    handleLinkClick={(e) => {
                      e.stopPropagation();
                      fireAnalytics(name, l3Name, analyticsData);
                      callNavigateAPIForExternalLinks(e);
                      hideL3Drawer(e);
                      hideL2Drawer(e, true);
                      closeNav();
                      setSelectedL1Index(-1);
                      if (shouldRenderMobileNavRedesignedView) {
                        setHideDrawerInfoSection(false);
                      }
                    }}
                    dataLocator={`l3_link_${index}`}
                  >
                    <BodyCopy
                      className="l2-nav-link"
                      fontFamily="secondary"
                      fontSize={['fs13', 'fs13', 'fs14']}
                      lineHeight="lh107"
                      color="text.primary"
                    >
                      <span
                        className={`${
                          shouldRenderMobileNavRedesignedView
                            ? 'nav-bar-item-label-mobile-redesign'
                            : 'nav-bar-item-label'
                        }${!promoBadge ? ' full-width' : ''}`}
                      >
                        {l3Name}
                      </span>
                      {renderPromoBadge(
                        promoBadge,
                        index,
                        brandThemeColor,
                        shouldRenderMobileNavRedesignedView
                      )}
                    </BodyCopy>
                  </Anchor>
                </li>
              );
            })}
          </ul>
        </Row>
      </div>
    </Drawer>
  );
};

L3Panel.propTypes = {
  id: PropTypes.string.isRequired,
  hideL3Drawer: PropTypes.func.isRequired,
  hideL2Drawer: PropTypes.func.isRequired,
  closeNav: PropTypes.func.isRequired,
  name: PropTypes.string.isRequired,
  shopalllink: PropTypes.string.isRequired,
  shopallaspath: PropTypes.string.isRequired,
  className: PropTypes.string.isRequired,
  links: PropTypes.arrayOf(
    PropTypes.shape({
      categoryContent: PropTypes.shape({
        displayToCustomer: PropTypes.bool,
        name: PropTypes.string,
        mainCategory: PropTypes.object,
      }),
      url: PropTypes.string,
      asPath: PropTypes.string,
    })
  ).isRequired,
  open: PropTypes.bool.isRequired,
  close: PropTypes.bool.isRequired,
  isShowNavAnimation: PropTypes.bool,
  setSelectedL1Index: PropTypes.func.isRequired,
  setHideDrawerInfoSection: PropTypes.func.isRequired,
  shouldRenderMobileNavRedesignedView: PropTypes.bool,
  analyticsData: PropTypes.string,
  brandThemeColor: PropTypes.string,
};

L3Panel.defaultProps = {
  isShowNavAnimation: false,
  analyticsData: '',
  shouldRenderMobileNavRedesignedView: false,
  brandThemeColor: '',
};

export { L3Panel as L3PanelVanilla };
export default withStyles(L3Panel, style);
